import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '~/lib/api';
import { useRecorder } from '~/lib/use-recorder';
import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/input';
import { Page } from '~/components/ui/page';

export const Route = createFileRoute('/_authed/today')({
  component: Today,
});

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'late';
  if (h < 12) return 'good morning';
  if (h < 18) return 'afternoon';
  if (h < 22) return 'evening';
  return 'late';
}

function Today() {
  const [text, setText] = useState('');
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const recorder = useRecorder();
  const qc = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: api.me });
  const entriesQuery = useQuery({
    queryKey: ['entries'],
    queryFn: () => api.listEntries(),
  });
  const yesterday = entriesQuery.data?.entries[0];

  const textMutation = useMutation({
    mutationFn: api.createTextEntry,
    onSuccess: () => {
      setText('');
      setConfirmation('saved.');
      qc.invalidateQueries({ queryKey: ['entries'] });
      setTimeout(() => setConfirmation(null), 2400);
    },
  });

  const voiceMutation = useMutation({
    mutationFn: (input: { blob: Blob; durationSeconds: number }) =>
      api.uploadVoice(input.blob, input.durationSeconds),
    onSuccess: () => {
      setConfirmation('saved.');
      qc.invalidateQueries({ queryKey: ['entries'] });
      setTimeout(() => setConfirmation(null), 2400);
    },
  });

  const busy =
    textMutation.isPending || voiceMutation.isPending || recorder.state === 'finishing';

  const firstName = meQuery.data?.user?.name?.split(' ')[0];

  return (
    <Page>
      <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-3">
        {greeting()}
        {firstName ? `, ${firstName.toLowerCase()}` : ''}
      </div>
      <h1 className="font-serif text-3xl sm:text-[34px] leading-tight">
        What&apos;s true right now?
      </h1>
      <p className="mt-2 text-sm text-muted-foreground max-w-prose">
        Type it, or hold the mic. Nothing here is shared. The system listens
        and remembers — quietly.
      </p>

      <form
        className="mt-8"
        onSubmit={(e) => {
          e.preventDefault();
          if (!text.trim()) return;
          textMutation.mutate({ text: text.trim() });
        }}
      >
        <Textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (confirmation) setConfirmation(null);
          }}
          rows={8}
          placeholder="…"
          autoFocus
          className="text-[17px] leading-[1.6]"
        />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={busy || !text.trim()} size="lg">
            {textMutation.isPending ? 'saving…' : 'Save'}
          </Button>
          <VoiceButton
            recorder={recorder}
            busy={busy}
            onCapture={(r) => voiceMutation.mutate(r)}
          />
          <Link
            to="/history"
            className="ml-auto text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            history →
          </Link>
        </div>

        <div className="mt-3 min-h-5 text-sm" aria-live="polite">
          {recorder.state === 'recording' && (
            <span className="text-[var(--color-accent)] font-medium">
              recording · {recorder.elapsed.toFixed(1)}s
            </span>
          )}
          {recorder.state === 'finishing' && (
            <span className="text-muted-foreground">transcribing…</span>
          )}
          {voiceMutation.isPending && recorder.state === 'idle' && (
            <span className="text-muted-foreground">transcribing…</span>
          )}
          {confirmation && recorder.state === 'idle' && !voiceMutation.isPending && (
            <span className="text-[var(--color-success)] animate-rise">{confirmation}</span>
          )}
          {recorder.error && (
            <span className="text-destructive">mic: {recorder.error}</span>
          )}
        </div>
      </form>

      {yesterday && (
        <section className="mt-12 border-t border-border pt-8">
          <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-3">
            last time you wrote
          </div>
          <p className="font-serif text-[17px] leading-[1.55] text-muted-foreground italic">
            “{yesterday.snippet}”
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {new Date(yesterday.createdAt).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </p>
        </section>
      )}
    </Page>
  );
}

function VoiceButton(props: {
  recorder: ReturnType<typeof useRecorder>;
  busy: boolean;
  onCapture: (r: { blob: Blob; durationSeconds: number }) => void;
}) {
  const isRecording = props.recorder.state === 'recording';
  return (
    <Button
      type="button"
      variant={isRecording ? 'destructive' : 'outline'}
      size="lg"
      disabled={props.busy && !isRecording}
      onClick={async () => {
        if (isRecording) {
          const r = await props.recorder.stop();
          if (r.blob.size > 0) props.onCapture(r);
        } else {
          await props.recorder.start();
        }
      }}
      leftIcon={
        <span
          aria-hidden
          className={`inline-block size-2 rounded-full ${
            isRecording ? 'bg-destructive-foreground animate-pulse' : 'bg-foreground/60'
          }`}
        />
      }
    >
      {isRecording ? 'Stop' : 'Record'}
    </Button>
  );
}
